import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import { useExperimentTagPool } from 'features/timeSeries/experiment/ExperimentLabContext'
import { FC, ReactNode } from 'react'
import styled from 'styled-components'
import { BaseSensorEntry } from 'types'

const short = (id: string) => id.split('-').slice(1).join('-') || id

/**
 * Per-pool id index, cached on the pool array itself so every TagLabel in a
 * render tree shares one O(1) lookup instead of each scanning the (up to
 * site-wide) entry list.
 */
const poolIndexCache = new WeakMap<
  BaseSensorEntry[],
  Map<string, BaseSensorEntry>
>()

const lookupEntry = (
  pool: BaseSensorEntry[],
  tagId: string
): BaseSensorEntry | undefined => {
  let index = poolIndexCache.get(pool)
  if (!index) {
    index = new Map(pool.map((sensor) => [sensor.id, sensor]))
    poolIndexCache.set(pool, index)
  }
  return index.get(tagId)
}

const Trigger = styled.span`
  display: inline-flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
`

const TipBody = styled.div`
  max-width: 18rem;
  text-align: left;
  line-height: 1.35;
  font-weight: 400;
  white-space: normal;
`

const TipName = styled.div`
  font-weight: 600;
`

const TipId = styled.div`
  opacity: 0.7;
  font-size: 0.9em;
  word-break: break-all;
`

export interface TagLabelProps {
  tagId: string
  /** Visible content; defaults to the shortened tag id. */
  children?: ReactNode
}

/**
 * A tag id rendered with a hover tooltip carrying the tag's full name and
 * description (the same rolodex metadata the All Tags tab searches), looked
 * up from the ScatterplotProvider's sensor entry pool via the Experiment Lab
 * context. Falls back to just the full id when no metadata exists, which
 * still beats a bare truncated label.
 */
export const TagLabel: FC<TagLabelProps> = ({ tagId, children }) => {
  const pool = useExperimentTagPool()
  const entry = lookupEntry(pool, tagId)
  const name = entry?.data?.name
  const description = entry?.data?.description

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <Trigger>{children ?? short(tagId)}</Trigger>
      </TooltipTrigger>
      <TooltipContent side='top' align='start' sideOffset={6}>
        <TipBody>
          <TipName>{name || short(tagId)}</TipName>
          {description && <div>{description}</div>}
          <TipId>{tagId}</TipId>
        </TipBody>
      </TooltipContent>
    </Tooltip>
  )
}
